/**
 * What Needs you's panes share: answering an ask (a refusal said once, the
 * list refreshed when the ask was closed already), the key hints, and what
 * the ask is about at the pane's top.
 */

import { useCallback, useState, type ReactNode } from 'react';

import { Badge, Group, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import * as Sentry from '@sentry/react';

import { ApiConflictError, vnextApiClient } from '@/services/vnext-api';
import {
  SECTION_NAMES,
  type Intervention,
  type InterventionAnswer,
  type Section,
} from '@/types/workbench';
import { toError } from '@/utils/errors';
import { AgentText } from '../AgentText';
import { failureMessage } from '../InterventionCard';
import classes from '../NeedsYou.module.css';

/** A key, as the hints show it. */
export function Kbd({ children }: Readonly<{ children: ReactNode }>) {
  return <span className={classes.kbd}>{children}</span>;
}

/** Answer asks: one, or several together (resolve-many). Done tells the
 * list which asks the person answered, so it doesn't count them as settled
 * by an agent; a refusal is said once; an ask closed already refreshes it. */
export function useAnswer(reportId: string, onAnswered: (ids: string[]) => void | Promise<void>) {
  const [submitting, setSubmitting] = useState(false);

  const submit = useCallback(
    async (ask: Intervention, answer: InterventionAnswer): Promise<boolean> => {
      setSubmitting(true);
      try {
        await vnextApiClient.resolveIntervention(reportId, ask.id, answer);
        await onAnswered([ask.id]);
        return true;
      } catch (err) {
        Sentry.captureException(toError(err));
        notifications.show({ color: 'red', message: failureMessage(err) });
        if (err instanceof ApiConflictError) {
          await onAnswered([]);
        }
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [reportId, onAnswered]
  );

  const submitMany = useCallback(
    async (answers: { ask: Intervention; answer: InterventionAnswer }[]): Promise<boolean> => {
      setSubmitting(true);
      try {
        const result = await vnextApiClient.resolveInterventions(
          reportId,
          answers.map(({ ask, answer }) => ({ interventionId: ask.id, answer }))
        );
        await onAnswered(result.resolved.map((r) => r.id));
        return true;
      } catch (err) {
        Sentry.captureException(toError(err));
        notifications.show({
          color: 'red',
          message: toError(err, 'Could not record the answers').message,
        });
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [reportId, onAnswered]
  );

  return { submitting, submit, submitMany };
}

/** The pane's top: the section, whose it is, the question and why. */
export function AskHeading({
  section,
  subjectName,
  question,
  why,
  blocking = false,
  extra,
}: Readonly<{
  section: Section;
  subjectName: string | null;
  question: string | null;
  why?: string | null;
  /** A run waits on the answer. */
  blocking?: boolean;
  extra?: ReactNode;
}>) {
  return (
    <Stack gap={4} data-testid="pane-heading">
      <Group gap={6} wrap="wrap">
        <Badge size="sm" variant="light" color="orange" style={{ textTransform: 'none' }}>
          {SECTION_NAMES[section]}
        </Badge>
        {subjectName && (
          <Text size="xs" c="dimmed">
            {subjectName}
          </Text>
        )}
        <Text size="xs" c="dimmed">
          · asked by the case agent
        </Text>
        {blocking && (
          <Badge size="xs" variant="light" color="red" style={{ textTransform: 'none' }}>
            holding up a run
          </Badge>
        )}
        {extra}
      </Group>
      {question && (
        <Text size="sm" fw={600} style={{ whiteSpace: 'pre-line' }}>
          <AgentText text={question} />
        </Text>
      )}
      {why && (
        <Text size="xs" c="dimmed" style={{ whiteSpace: 'pre-line' }}>
          <AgentText text={why} />
        </Text>
      )}
    </Stack>
  );
}

export function listOf(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

export function textOf(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}
